import styled from 'styled-components';
import getBaseByProps from '@userzoomlibraries/toolkit-theme-manager/get-base-by-props';
import getThemeByProps from '@userzoomlibraries/toolkit-theme-manager/get-theme-by-props';
import React, { useContext, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import isDefinedAndNotNull from '@userzoomlibraries/toolkit-js-utils/is-defined-and-not-null';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import canUseDOM from '@userzoomlibraries/toolkit-js-utils/can-use-dom';
import getSafeHTMLElement from '@userzoomlibraries/toolkit-js-utils/get-safe-html-element';
import toCamelCase from '@userzoomlibraries/toolkit-js-utils/to-camel-case';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import useIsomorphicEffect from '@userzoomlibraries/toolkit-react-utils/use-isomorphic-effect';
import isFunction from '@userzoomlibraries/toolkit-js-utils/is-function';
import isString from '@userzoomlibraries/toolkit-js-utils/is-string';
import getSafeDocumentBody from '@userzoomlibraries/toolkit-js-utils/get-safe-document-body';
import { jsx } from 'react/jsx-runtime';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var PortalContext = /*#__PURE__*/React.createContext(getSafeDocumentBody());

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var usePortalContext = function usePortalContext() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$appendTo = _ref.appendTo,
      appendTo = _ref$appendTo === void 0 ? null : _ref$appendTo;

  var portalContext = useContext(PortalContext);

  if (appendTo) {
    if (isFunction(appendTo)) {
      return appendTo();
    }

    if (isString(appendTo) && canUseDOM()) {
      return document.getElementById(appendTo);
    }

    return appendTo;
  }

  return portalContext || getSafeDocumentBody();
};

var _excluded = ["id", "className", "appendTo", "children", "onAnimationEnd", "onTransitionEnd"];

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var Portal$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
      className = _ref.className,
      appendTo = _ref.appendTo,
      children = _ref.children,
      onAnimationEnd = _ref.onAnimationEnd,
      onTransitionEnd = _ref.onTransitionEnd,
      rest = _objectWithoutProperties(_ref, _excluded);

  var dataAttributes = useDataAttributes(rest);
  var parentElement = usePortalContext({
    appendTo: appendTo
  });

  var _useState = useState(function () {
    if (canUseDOM()) {
      var element = document.getElementById(id);

      if (!element) {
        element = document.createElement('div');

        if (isDefinedAndNotNull(id)) {
          element.setAttribute('id', id);
        }

        element.className = className;
        Object.keys(dataAttributes).forEach(function (key) {
          var keyWithoutData = key.replace('data-', '');
          var finalKey = toCamelCase(keyWithoutData);
          element.dataset[finalKey] = dataAttributes[key];
        });
        element.onanimationend = onAnimationEnd;
        element.ontransitionend = onTransitionEnd;
      }

      return element;
    } // SSR Case


    // SSR Case
    return null;
  }),
      _useState2 = _slicedToArray(_useState, 1),
      portalElement = _useState2[0];

  useIsomorphicEffect(function () {
    if (portalElement && parentElement) {
      parentElement.appendChild(portalElement);
      return function () {
        parentElement.removeChild(portalElement);
      };
    }

    return noop;
  }, [portalElement, parentElement]);
  useIsomorphicEffect(function () {
    if (portalElement) {
      if (isDefinedAndNotNull(id)) {
        portalElement.setAttribute('id', id);
      }

      portalElement.className = className;
      Object.keys(dataAttributes).forEach(function (key) {
        var keyWithoutData = key.replace('data-', '');
        var finalKey = toCamelCase(keyWithoutData);
        portalElement.dataset[finalKey] = dataAttributes[key];
      });
      portalElement.onanimationend = onAnimationEnd;
      portalElement.ontransitionend = onTransitionEnd;
    }
  }, [id, className, portalElement, dataAttributes, onAnimationEnd, onTransitionEnd]);

  if (portalElement) {
    var portalJSX = /*#__PURE__*/jsx(PortalContext.Provider, {
      value: portalElement,
      children: /*#__PURE__*/jsx("div", {
        ref: ref,
        children: children
      })
    });

    return /*#__PURE__*/ReactDOM.createPortal(portalJSX, portalElement);
  } // SSR Case


  return null;
});
Portal$1.COMPONENT_ID = 'Gearz.Portal';
Portal$1.displayName = Portal$1.COMPONENT_ID;
Portal$1.propTypes = {
  id: PropTypes.stringWithDescription('Custom id of the portal.'),
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  appendTo: PropTypes.oneOfTypeWithDescription([PropTypes.func, PropTypes.string, PropTypes.instanceOf(getSafeHTMLElement())], 'Destination element to append the portal.'),
  children: PropTypes.nodeWithDescription('Content of the portal.').isRequired,
  onAnimationEnd: PropTypes.funcWithDescription('Callback called when animationend event is triggered.'),
  onTransitionEnd: PropTypes.funcWithDescription('Callback called when transitionend event is triggered.')
};
Portal$1.defaultProps = {
  id: null,
  className: '',
  appendTo: null,
  onAnimationEnd: noop,
  onTransitionEnd: noop
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Portal = styled(Portal$1).withConfig({
  componentId: "gz-portal-1679041969106__sc-1r1zngf-0"
})(["", " ", ""], function (props) {
  return getBaseByProps({
    props: props
  });
}, function (props) {
  return getThemeByProps({
    componentId: Portal$1.COMPONENT_ID,
    props: props
  });
});
Portal.propTypes = Portal$1.propTypes;
Portal.defaultProps = Portal$1.defaultProps;

export { Portal as P };
